import {
  buildOrderPreviewRequest,
  isRowPreviewable,
  PriceAnchor,
} from '@/features/order/order-form/lib/build-order-preview-request'
import { AnchorPrice, OffsetUnit } from '@/features/order/order-form/lib/resolve-anchor-price'
import { UnifiedItemValues } from '@/features/order/order-form/lib/unified-order-schema'
import { parseExchangeField } from '@/lib/proto/util'
import { Exchange, Instrument } from '@/types/generated/common/types'
import { BatchOrderPreviewRequest } from '@/types/generated/public_api'

const MAX_BATCH_ROWS = 20

export interface BuildBatchOrderPreviewRequestArgs {
  items: UnifiedItemValues[]
  formPrice: string
  priceAnchor: PriceAnchor
  /** Only meaningful when priceAnchor is relative — see
   * buildOrderPreviewRequest's own doc comment. */
  priceOffsetUnit?: OffsetUnit
  isMarketOrder: boolean
  isLimitChaseMode: boolean
  /** Rows can span exchanges (multi-account batch preview), so denomination/
   * contract-size must resolve against EACH ROW'S OWN exchange's instrument
   * list, not one exchange's list applied to every row — same fix
   * unified-order-form.tsx already applies via fieldExchanges/
   * getFieldInstrument. Keyed by the resolved `Exchange` enum; a row whose
   * own exchange has no entry (still loading, or genuinely empty) falls back
   * to an empty array, same as a missing instrument today. */
  instrumentsByExchange: Map<Exchange, Instrument[]>
  /** Fallback exchange for a row with no `exchange` of its own (see
   * parseExchangeField) — the active account's primary exchange. */
  primaryExchange: Exchange
  /** Each row's own live best bid/ask, keyed by symbol — a relative anchor
   * (mid/best_bid/best_ask) resolves per-row against ITS OWN symbol's
   * ticker, not the ticket's first row, so a multi-leg batch prices each
   * leg correctly instead of all legs sharing one price. */
  tickerBySymbol?: Record<string, AnchorPrice | undefined>
  /** Each row's own tick size, keyed by symbol — for rounding the resolved
   * price, see computeEstimatedPrice. */
  tickSizeBySymbol?: Record<string, number | undefined>
}

/**
 * Builds a BatchOrderPreviewRequest covering every row with a valid,
 * previewable state — rows still missing a symbol/account are silently
 * dropped rather than failing the whole batch, same tolerance
 * `buildOrderPreviewRequest` already has for the single-row case. A row with
 * no amount typed yet is still included, previewed against a nominal
 * placeholder quantity (see `resolvePreviewAmount` in build-order-preview-
 * request.ts) purely so its max-size hint has something to show before the
 * first keystroke. Returns `null` when no row is previewable yet. Caps at 20
 * rows (server-enforced too — see `order_preview_batch.rs`), dropping any
 * rows beyond that rather than erroring, since the cap is a resource guard,
 * not a form-validity concern the user needs to react to mid-typing.
 */
export function buildBatchOrderPreviewRequest(
  args: BuildBatchOrderPreviewRequestArgs
): BatchOrderPreviewRequest | null {
  const {
    items,
    formPrice,
    priceAnchor,
    priceOffsetUnit,
    isMarketOrder,
    isLimitChaseMode,
    instrumentsByExchange,
    primaryExchange,
    tickerBySymbol,
    tickSizeBySymbol,
  } = args
  const orders = items
    .map((item) =>
      buildOrderPreviewRequest({
        item,
        formPrice,
        priceAnchor,
        priceOffsetUnit,
        isMarketOrder,
        isLimitChaseMode,
        accountId: item.accountId ?? '',
        instruments:
          instrumentsByExchange.get(parseExchangeField(item.exchange, primaryExchange)) ?? [],
        ticker: item.symbol ? tickerBySymbol?.[item.symbol] : undefined,
        tickSize: item.symbol ? tickSizeBySymbol?.[item.symbol] : undefined,
      })
    )
    .filter((req): req is NonNullable<typeof req> => req !== null)
    .slice(0, MAX_BATCH_ROWS)

  if (orders.length === 0) return null
  return { orders }
}

/**
 * Maps each item's index in `items` to its row index in the resulting
 * request's `orders` — and therefore in `BatchOrderPreviewResponse.rows`,
 * which the server returns index-for-index with the request — or
 * `undefined` if that item was dropped. Shares `isRowPreviewable` with
 * buildOrderPreviewRequest's own drop condition (see build-order-preview-
 * request.ts) so the two can't drift apart, plus the MAX_BATCH_ROWS cap.
 *
 * Rows without a computable preview are silently excluded from the request
 * (see buildBatchOrderPreviewRequest above), so a row's position among
 * `items` and its position in the batch response diverge as soon as any
 * earlier row is incomplete. Callers that zip a response row back to its
 * input (e.g. a per-row max-size hint) must go through this rather than
 * assuming the two index spaces line up.
 */
export function mapItemsToBatchRowIndex(
  items: (UnifiedItemValues | undefined)[]
): (number | undefined)[] {
  let nextRowIndex = 0
  return items.map((item) => {
    const accountId = item?.accountId ?? ''
    if (!isRowPreviewable(item, accountId) || nextRowIndex >= MAX_BATCH_ROWS) return undefined
    return nextRowIndex++
  })
}
